/**
 * QuickInfoBar — the two-column Config | Variants footer every image-producing
 * node card carries.
 *
 *   - Config: a config summary + reference thumbnails. Click → Config panel.
 *   - Variants: label + a status dot + the variant count. Click → the dedicated
 *     Variants panel (tab-less, canvas-zoom).
 *
 * ## Two presentations, one component
 *
 * The bar shipped twice: extracted here for ImageGenerator / VideoGenerator /
 * GroupNode (post 1533007285123236), and hand-inlined three more times in
 * CharacterDesigner, LocationDesigner and ObjectDesigner. The copies were not
 * accidental — each accumulated its own fixes — so they had genuinely diverged
 * by the time Canvas UI 2.0 came to restyle them.
 *
 *   `compact`  — fixed-height bar, config summary in a truncating pill with a
 *                hover-expanded overlay, narrow 72px variants column.
 *   `designer` — intrinsic height, plain full-size truncating summary text,
 *                96px variants column (its labels are `text-sm`), inset to
 *                match the designer card's px-6 gutter.
 *
 * `designer` is what the Canvas UI 2.0 frames draw, which is why the three
 * designers were folded onto THIS component rather than onto `compact`. The
 * intent is one presentation eventually; `compact` survives because its own
 * frame has not landed and its geometry carries several Playwright specs
 * (flush corners, bottom padding, column centering, thumbnail clearance).
 * When that frame arrives, delete a branch — do not add a third.
 *
 * Testids are stable across both (`quick-info-bar`, `quick-info-config`,
 * `quick-info-config-label`, `quick-info-config-summary`,
 * `quick-info-variants`); `compact` additionally exposes
 * `quick-info-config-summary-expanded` for its hover overlay.
 */

import { memo, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { NodeStatus } from '../../../../generated/nodeDataTypes'
import { cn } from '../../../../lib/utils'
import { useDebugLog } from '../../../../utils/debugLog'
import { CANVAS_NODE_QUICK_INFO_INTRINSIC_HEIGHT } from './constants'
import type { SelectedReference } from './ReferenceSelector'
import { ReferenceThumbnailButton } from './ReferenceMediaPreview'
import { AudioReferenceThumbnail } from './AudioReferenceLabelChip'

export type QuickInfoBarPresentation = 'compact' | 'designer'

export interface QuickInfoVoiceReference {
  nodeId: string
  label: string
  status?: 'ready' | 'pending'
  actionLabel?: string
}

export interface QuickInfoBarProps {
  /** Single-line config summary (e.g. "Model · 16:9 · 1K"). Already display-formatted. */
  configSummary: string
  /**
   * Reference chips to preview.
   *
   * OMIT entirely on a node with no reference images (ScriptWriter) — the
   * reference row is then not rendered at all. Audio entries render as compact
   * waveform chips because Video Generator audio references are graph inputs,
   * not image thumbnails.
   */
  references?: SelectedReference[]
  /** Optional primary voice shortcut rendered between Config and Variants. */
  primaryVoiceReference?: QuickInfoVoiceReference
  /** Canonical execution status for the node that owns this bar. */
  nodeStatus?: NodeStatus
  /** Project ID for thumbnail URL construction. */
  projectId?: string
  /** Cache-bust key for thumbnail URLs. */
  cacheBust?: string
  /** Number of generated variants. */
  variantCount: number
  /** Open the Config (tabbed) panel. */
  onOpenConfig: () => void
  /** Open the dedicated Variants panel. */
  onOpenVariants: () => void
  /** Open or select the primary linked voice node. */
  onOpenVoiceReference?: (nodeId: string) => void
  /** Which of the two presentations to render. Defaults to `compact`. */
  presentation?: QuickInfoBarPresentation
  /**
   * Dot colour once at least one variant exists.
   *
   * The designers key this to their node-type identity colour
   * (`agentColor.active`) — the same colour the card border and the variant
   * picker use — so a required-looking default of `var(--primary)` would
   * silently flatten that. Defaults to `var(--primary)` for `compact`, whose
   * callers have no per-node identity colour.
   */
  accentColor?: string
  /** How many reference thumbnails to preview. Defaults to 2. */
  maxReferences?: number
  /**
   * Source tag for the `dw:config-label-footer` NAD trace, so a footer-open in
   * the log says which card it came from.
   */
  logSource?: string
  /**
   * Which column currently has the bottom panel open, filled like an active
   * tab. Optional: callers that omit it render unhighlighted, which is the
   * behaviour every caller had before this prop existed.
   */
  activeColumn?: 'config' | 'variants' | null
  /**
   * Split the bar into two EQUAL columns instead of sizing Variants to its own
   * content. Defaults to false, so the image nodes are unaffected.
   *
   * Not a third presentation — the contract above says to delete a branch
   * rather than add one, and this is a parameter of the existing two.
   *
   * The comment on the grid below argues an even split cannot work because
   * Config's summary is open-ended (`Model · Time · Weather`). That holds for
   * the image nodes and does NOT hold for Script Writer, whose summary is only
   * ever a short model name (`Script Lite`). Its design (Figma 133:241) divides
   * the card exactly in half: a 1168-wide card with the divider at 584.
   */
  equalColumns?: boolean
}

function selectVisibleMediaReferences(
  references: SelectedReference[],
  maxReferences: number,
): SelectedReference[] {
  if (maxReferences <= 0) return []
  const visible = references.slice(0, maxReferences)
  if (visible.some((ref) => ref.mediaType === 'audio')) return visible

  const firstAudio = references.find((ref) => ref.mediaType === 'audio')
  if (!firstAudio) return visible
  if (visible.length < maxReferences) return [...visible, firstAudio]
  return [...visible.slice(0, Math.max(0, maxReferences - 1)), firstAudio]
}

const QuickInfoBar = memo(function QuickInfoBar({
  configSummary,
  references,
  primaryVoiceReference,
  nodeStatus,
  projectId,
  cacheBust,
  variantCount,
  onOpenConfig,
  onOpenVariants,
  onOpenVoiceReference,
  presentation = 'compact',
  accentColor = 'var(--primary)',
  maxReferences = 2,
  logSource = 'quick-info-bar',
  activeColumn = null,
  equalColumns = false,
}: QuickInfoBarProps) {
  const { t } = useTranslation('canvas')
  const log = useDebugLog('dw:config-label-footer:quickinfobar')
  const isDesigner = presentation === 'designer'
  const configActive = activeColumn === 'config'
  const variantsActive = activeColumn === 'variants'
  const hasPrimaryVoiceReference = Boolean(primaryVoiceReference)
  const primaryVoiceIsAction = Boolean(primaryVoiceReference?.actionLabel)
  const previewReferences = references?.filter(
    (ref) => ref.mediaType === 'image' || ref.mediaType === 'video' || ref.mediaType === 'audio',
  ) ?? []
  const visibleMediaReferences = selectVisibleMediaReferences(previewReferences, maxReferences)
  const visibleReferenceCount = visibleMediaReferences.length
  const visibleAudioReferenceLabels = useMemo(
    () => visibleMediaReferences
      .filter((ref) => ref.mediaType === 'audio')
      .map((ref) => ref.label),
    [visibleMediaReferences],
  )

  const audioReferenceLabelLog = useDebugLog('dw:audio-reference-labels')
  useEffect(() => {
    if (visibleAudioReferenceLabels.length === 0) return
    audioReferenceLabelLog({
      event: 'audio-reference-labels:quick-info-visible',
      source: logSource,
      audio_reference_count: visibleAudioReferenceLabels.length,
      audio_reference_labels: visibleAudioReferenceLabels,
    })
  }, [audioReferenceLabelLog, logSource, visibleAudioReferenceLabels])

  // ALWAYS out of grid flow (`absolute`), and nothing at all when there is
  // nothing to preview. The bar below declares exactly two columns, so it must
  // contain exactly two in-flow children — Config and Variants. A third one
  // takes the Variants cell and pushes that button onto an implicit SECOND row,
  // which `compact`'s fixed 80px height then crushes and clips.
  //
  // That is why the empty case renders nothing rather than a placeholder.
  // `compact` used to emit an em-dash here "so the fixed-height bar does not
  // reflow when references come and go" — which made sense when the populated
  // branch was in flow too, because then BOTH states wrapped and the layout was
  // at least consistent. #3849 moved the populated branch to `absolute` and left
  // the placeholder behind, making it the only remaining thing that could wrap
  // the bar; a video generator with no references wired hit that every time.
  // The bar is fixed-height anyway, so there is no reflow left to guard against.
  // `designer` has always rendered nothing here.
  const thumbnails =
    visibleReferenceCount > 0 ? (
      <div
        className={cn(
          'absolute z-20 flex items-center gap-1 overflow-hidden',
          isDesigner
            ? hasPrimaryVoiceReference
              ? 'bottom-3 left-6 right-[220px]'
              : 'bottom-3 left-6 right-[108px]'
            : 'bottom-1.5 left-2 right-20',
        )}
        data-testid="quick-info-reference-thumbnails"
      >
        {visibleMediaReferences.map((ref, i) => (
          <ReferenceThumbnailButton
            key={`${ref.mediaType}-${ref.nodeId}-${ref.fromIndex ?? i}`}
            reference={ref}
            audioTestId={`quick-info-audio-reference-${ref.nodeId}`}
            projectId={projectId}
            cacheBust={cacheBust}
            surface="quick-info-reference"
            className="h-6 w-6 rounded border border-border"
          />
        ))}
      </div>
    ) : null

  const handleOpenConfig = (e: React.MouseEvent) => {
    e.stopPropagation()
    log({
      event: 'config-open',
      source: logSource,
      label: t('quickInfoBar.config'),
      summary: configSummary,
    })
    onOpenConfig()
  }

  const handleOpenVariants = (e: React.MouseEvent) => {
    e.stopPropagation()
    onOpenVariants()
  }

  const handleOpenVoiceReference = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!primaryVoiceReference || !onOpenVoiceReference) return
    onOpenVoiceReference(primaryVoiceReference.nodeId)
  }

  return (
    <div
      data-testid="quick-info-bar"
      data-presentation={presentation}
      // `compact` height is single-sourced from
      // CANVAS_NODE_QUICK_INFO_INTRINSIC_HEIGHT so the rendered bar height can
      // never desync from the `contain-intrinsic-size` reservation its cards
      // use (prevents a select/deselect jump). `designer` cards make no such
      // reservation and size to content.
      style={isDesigner ? undefined : { height: CANVAS_NODE_QUICK_INFO_INTRINSIC_HEIGHT }}
      // Both presentations pin the Variants column to the width its own content
      // needs and give Config the rest. An even 50/50 split cannot work here:
      // Config's summary is open-ended (`Model · Time · Weather`) while Variants
      // is a fixed label plus a count, so half the bar is simultaneously too
      // little for one and too much for the other. `designer` needs the wider
      // 96px because its labels are `text-sm` — 72px would elide the Vietnamese
      // `Phiên bản` (56.9px) once its 24px `px-3` gutter is taken out.
      // ...unless `equalColumns` is set: see that prop for why Script Writer is
      // the exception to the reasoning above.
      // `shrink-0` is load-bearing, not defensive. ScriptWriter is the only card
      // that is itself a `flex flex-col` with a `max-height` (620px), so the bar
      // is a flex item there — and its own `overflow-hidden` drops its automatic
      // minimum size to 0, which lets flexbox squash the 80px bar to ~28px and
      // clip the config summary in half. The image/designer cards never hit this
      // because their cards are blocks with no height clamp.
      className={cn(
        'relative grid shrink-0 overflow-hidden border-t border-border-subtle',
        isDesigner ? 'px-6' : 'bg-surface-0',
        equalColumns
          ? 'grid-cols-2'
          : isDesigner && hasPrimaryVoiceReference
            ? 'grid-cols-[minmax(0,1fr)_112px_96px]'
            : isDesigner
            ? 'grid-cols-[minmax(0,1fr)_96px]'
            : 'grid-cols-[minmax(0,1fr)_72px]',
      )}
    >
      {/* Config Column */}
      <button
        data-testid="quick-info-config"
        data-active={configActive || undefined}
        title={configSummary}
        onClick={handleOpenConfig}
        className={cn(
          isDesigner
            ? cn(
                'min-w-0 py-3 flex flex-col items-start gap-1.5 transition-colors nodrag border-r border-border-subtle pr-3',
                visibleReferenceCount > 0 ? 'pb-11' : '',
              )
            : cn(
                'group relative z-10 min-w-0 overflow-visible py-2.5 px-2 flex flex-col items-start gap-1 transition-colors nodrag border-r border-border-subtle',
                // Top-aligned (not centred) so the CONFIG label lines up with
                // VARIANTS: the two columns hold different-height content
                // (a summary pill vs a count), so centring offset the labels.
                'justify-start',
                visibleReferenceCount > 0 ? 'pb-10' : '',
              ),
          configActive ? 'bg-primary text-primary-foreground' : 'hover:bg-surface-1',
        )}
      >
        {/* "Config" label — mirrors the Variants column label so first-time
            users can tell this is the configuration column (post 1560145292409435).
            Both labels render in caps via CSS, matching the config panel's
            tab labels; the locale strings stay as-is. */}
        <span
          data-testid="quick-info-config-label"
          className={
            isDesigner
              ? 'text-sm font-medium uppercase tracking-wider text-muted-foreground'
              : 'text-xxs uppercase tracking-wider text-muted-foreground leading-none'
          }
        >
          {t('quickInfoBar.config')}
        </span>

        {isDesigner ? (
          // `max-w-full`/`min-w-0` are what make `truncate` bite: this span is a
          // flex item of a `flex-col items-start` button, so on its own it sizes
          // to the nowrap text's max-content width and paints over the Variants
          // column instead of eliding (T286810882).
          <span
            data-testid="quick-info-config-summary"
            className="block max-w-full min-w-0 text-sm font-medium text-muted-foreground truncate"
          >
            {configSummary}
          </span>
        ) : (
          // Plain muted text like the designer cards' model name: no pill at
          // rest or on hover. A truncated summary is still readable through the
          // button's native `title` tooltip.
          <span
            data-testid="quick-info-config-summary"
            className="block max-w-full min-w-0 truncate text-xs font-medium text-muted-foreground"
          >
            {configSummary}
          </span>
        )}
      </button>

      {thumbnails}

      {primaryVoiceReference && (
        <button
          type="button"
          data-testid="character-linked-voice-chip"
          data-nux-anchor={primaryVoiceIsAction ? 'character-add-voice' : undefined}
          data-nux-node-id={primaryVoiceIsAction ? primaryVoiceReference.nodeId : undefined}
          data-nux-node-type={primaryVoiceIsAction ? 'characterDesigner' : undefined}
          data-nux-node-status={primaryVoiceIsAction ? nodeStatus : undefined}
          aria-label={
            primaryVoiceIsAction
              ? primaryVoiceReference.label
              : primaryVoiceReference.status === 'pending'
              ? `Open voice reference: ${primaryVoiceReference.label}. Sample pending.`
              : `Open voice reference: ${primaryVoiceReference.label}.`
          }
          title={
            primaryVoiceIsAction
              ? primaryVoiceReference.label
              : primaryVoiceReference.status === 'pending'
              ? `${primaryVoiceReference.label} sample pending`
              : primaryVoiceReference.label
          }
          onClick={handleOpenVoiceReference}
          disabled={!onOpenVoiceReference}
          className={cn(
            isDesigner
              ? cn(
                  'min-w-0 border-r border-border-subtle px-3 py-3 flex flex-col gap-1.5 transition-colors nodrag',
                  primaryVoiceIsAction ? 'items-start' : 'items-center justify-center',
                )
              : cn(
                  'min-w-0 overflow-hidden border-r border-border-subtle px-2 py-2.5 flex flex-col justify-center gap-1 transition-colors nodrag',
                  primaryVoiceIsAction ? 'items-start' : 'items-center',
                ),
            onOpenVoiceReference ? 'hover:bg-surface-1' : 'cursor-default',
            !primaryVoiceIsAction && 'group/voice',
          )}
        >
          {primaryVoiceReference.actionLabel ? (
            <>
              <span
                data-testid="character-linked-voice-title"
                className={
                  isDesigner
                    ? 'text-sm font-medium uppercase tracking-wider text-muted-foreground'
                    : 'text-xxs text-muted-foreground leading-none'
                }
              >
                {t('quickInfoBar.voice')}
              </span>
              <span
                data-testid="character-linked-voice-action"
                className="block max-w-full min-w-0 truncate text-xs font-medium text-muted-foreground"
              >
                {primaryVoiceReference.actionLabel}
              </span>
            </>
          ) : (
            <AudioReferenceThumbnail
              label={primaryVoiceReference.label}
              testId="character-linked-voice-waveform"
              referenceNodeId={primaryVoiceReference.nodeId}
              surface="primary-voice-reference"
              className={cn(
                isDesigner ? 'h-[45px] w-[45px]' : 'h-9 w-9',
                'rounded-lg transition-colors group-hover/voice:border-input-border-hover group-hover/voice:bg-surface-3',
              )}
            />
          )}
        </button>
      )}

      {/* Variants Column */}
      <button
        data-testid="quick-info-variants"
        data-active={variantsActive || undefined}
        onClick={handleOpenVariants}
        className={cn(
          isDesigner
            ? 'px-3 py-3 flex flex-col items-start gap-1.5 transition-colors nodrag'
            : 'min-w-0 overflow-hidden py-2.5 px-2 flex flex-col items-start justify-start gap-1 transition-colors nodrag',
          variantsActive ? 'bg-primary text-primary-foreground' : 'hover:bg-surface-1',
        )}
      >
        <span
          data-testid="quick-info-variants-label"
          className={
            isDesigner
              ? 'text-sm font-medium uppercase tracking-wider text-muted-foreground'
              : 'text-xxs uppercase tracking-wider text-muted-foreground leading-none'
          }
        >
          {t('quickInfoBar.variants')}
        </span>
        <div className="flex items-center gap-1.5">
          {/* The dot reads as "has variants" against the surface fill; on the
              active (filled) column it would be an indistinct blob. */}
          {!variantsActive && (
          <span
            className="w-2 h-2 rounded-full"
            style={{
              backgroundColor: variantCount > 0 ? accentColor : 'var(--text-muted)',
            }}
          />
          )}
          <span
            data-testid="quick-info-variants-count"
            className={
              isDesigner
                ? 'text-sm font-medium text-muted-foreground'
                : 'text-xs font-medium text-muted-foreground'
            }
          >
            {variantCount}
          </span>
        </div>
      </button>
    </div>
  )
})

QuickInfoBar.displayName = 'QuickInfoBar'

export { QuickInfoBar }
